import React from 'react';

import { CssBaseline, GlobalStyles } from '@mui/material';
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';

import geistVariableFont from 'assets/fonts/Geist-Variable.woff2';

const SYSTEM_SANS_FALLBACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const SYNMATCH_FONT_FAMILY = `"Geist", "Geist Variable", ${SYSTEM_SANS_FALLBACK}`;

export const SYNMATCH_COLORS = {
  primary: '#00234b',
  accent: '#9bbbe6',
  // Resting underline for an accent action on an inverse surface, where the
  // accent alone is 1.97:1 against the message text beside it.
  accentMuted: 'rgba(155, 187, 230, 0.4)',
  // Reserved marketing/data-viz accent — no semantic home on the triage board,
  // so it is intentionally unused across the applications UI in SM-38.
  cerise: '#E93B80',
  white: '#FFFFFF',
  text: {
    default: '#0A1628',
    muted: '#4B5563',
    onInverse: '#FFFFFF',
    onInverseMuted: '#CFDCEA',
  },
  surface: {
    app: '#F7F8FA',
    default: '#FFFFFF',
    inverseHover: 'rgba(255, 255, 255, 0.06)',
    // Sticky-header scrim: high alpha (~0.88) keeps the bar legible without a
    // backdrop-filter fallback, so no @supports rule needed.
    headerScrim: 'rgba(255, 255, 255, 0.88)',
  },
  border: {
    // Platinum, the brand's own neutral.
    default: '#DADFE4',
    inverse: 'rgba(255, 255, 255, 0.14)',
  },
  // Attention marker for inverse surfaces. palette.error.main is only 2.58:1 on
  // Oxford and fails the 3:1 non-text minimum, so the on-dark signal is a
  // brightened sibling at 4.3:1.
  signal: '#E4536F',
  status: {
    success: { bg: '#D7EDD9', fg: '#1F4D2A' },
    warn: { bg: '#FFF1D6', fg: '#6A4A0E' },
    error: { bg: '#FADCE5', fg: '#7A1933' },
    info: { bg: '#E2EDFA', fg: '#052B50' },
    neutral: { bg: '#EFF1F4', fg: '#4B5563' },
  },
} as const;

export const SYNMATCH_RADII = {
  sm: '0.375rem',
  md: '0.625rem',
  lg: '0.875rem',
  pill: '999rem',
} as const;

const SYNMATCH_SCRIM = 'rgba(0, 35, 64, 0.55)';

// Also exported standalone for the few styled components that render outside a
// theme provider, so the value has one definition rather than a literal twin.
export const SYNMATCH_HOVER_TRANSITION = '160ms cubic-bezier(0.2, 0, 0.2, 1)';

// The one way an action says "temporarily unusable" while it must keep the
// keyboard: the real `disabled` attribute would drop focus mid-flight, so the
// control stays focusable with `aria-disabled` and borrows the disabled look.
const INERT_ACTION_SX = {
  color: 'action.disabled',
  pointerEvents: 'none',
  '&.MuiButton-contained': {
    backgroundColor: 'action.disabledBackground',
    boxShadow: 'none',
  },
  '&.MuiButton-outlined': {
    borderColor: 'action.disabledBackground',
  },
} as const;

// Announcement, suppression, and look are one primitive, so a caller cannot
// wire a control that looks unusable but still fires, or the reverse.
export const inertActionProps = (isInert: boolean, onActivate: () => void) => ({
  'aria-disabled': isInert || undefined,
  onClick: isInert ? undefined : onActivate,
  sx: isInert ? INERT_ACTION_SX : undefined,
});

export const SYNMATCH_SHADOWS = {
  card: '0 0.0625rem 0.125rem rgba(0, 35, 64, 0.06), 0 0.0625rem 0.1875rem rgba(0, 35, 64, 0.08)',
  cardHover:
    '0 0.25rem 0.75rem rgba(0, 35, 64, 0.08), 0 0.125rem 0.25rem rgba(0, 35, 64, 0.04)',
  modal: '0 1.5rem 4rem rgba(0, 35, 64, 0.24)',
} as const;

// Scoped applications-surface extras. The full ramp/motion/surface system and
// the complete override matrix stay the dedicated theming slice; this adds only
// the tokens SM-38 consumes, exposed through `theme.synmatch` so styled
// components read them via the theme instead of raw hex.
export interface SynmatchThemeExtras {
  radius: { lg: string; pill: string };
  shadow: { card: string; cardHover: string; modal: string };
  scrim: string;
  focusRing: string;
  transition: { hover: string };
  avatar: { background: string; color: string };
}

const synmatchThemeExtras: SynmatchThemeExtras = {
  radius: { lg: SYNMATCH_RADII.lg, pill: SYNMATCH_RADII.pill },
  shadow: SYNMATCH_SHADOWS,
  scrim: SYNMATCH_SCRIM,
  // Jordy is the brand focus accent for the dark Oxford sidebar (accessible on
  // navy). On light surfaces the accessible indicator is the Oxford outline; do
  // not use this alone as a focus ring on white (it is ~1.6:1).
  focusRing: SYNMATCH_COLORS.accent,
  transition: { hover: SYNMATCH_HOVER_TRANSITION },
  avatar: {
    background: SYNMATCH_COLORS.accent,
    color: SYNMATCH_COLORS.primary,
  },
};

declare module '@mui/material/styles' {
  interface Theme {
    synmatch: SynmatchThemeExtras;
  }

  interface ThemeOptions {
    synmatch?: SynmatchThemeExtras;
  }
}

// Both alert densities read the same status tokens. Without this the standard
// alert falls through to MUI's own variant, which lightens palette.<severity>.light
// by 0.9 into near-white.
const alertSeverityStyles = (status: { bg: string; fg: string }) => ({
  backgroundColor: status.bg,
  border: `0.0625rem solid ${status.bg}`,
  color: SYNMATCH_COLORS.text.default,
  '& .MuiAlert-icon': { color: status.fg },
});

export const synmatchTheme = createTheme({
  synmatch: synmatchThemeExtras,
  palette: {
    primary: {
      main: SYNMATCH_COLORS.primary,
      contrastText: SYNMATCH_COLORS.white,
    },
    success: {
      main: '#2F7A3D',
      dark: SYNMATCH_COLORS.status.success.fg,
      light: SYNMATCH_COLORS.status.success.bg,
      contrastText: SYNMATCH_COLORS.white,
    },
    warning: {
      main: '#B8770D',
      dark: SYNMATCH_COLORS.status.warn.fg,
      light: SYNMATCH_COLORS.status.warn.bg,
      contrastText: SYNMATCH_COLORS.text.default,
    },
    error: {
      main: '#B82A4A',
      dark: SYNMATCH_COLORS.status.error.fg,
      light: SYNMATCH_COLORS.status.error.bg,
      contrastText: SYNMATCH_COLORS.white,
    },
    info: {
      main: SYNMATCH_COLORS.status.info.fg,
      dark: SYNMATCH_COLORS.primary,
      light: SYNMATCH_COLORS.status.info.bg,
      contrastText: SYNMATCH_COLORS.white,
    },
    background: {
      default: SYNMATCH_COLORS.surface.app,
      paper: SYNMATCH_COLORS.surface.default,
    },
    divider: SYNMATCH_COLORS.border.default,
    text: {
      primary: SYNMATCH_COLORS.text.default,
      secondary: SYNMATCH_COLORS.text.muted,
    },
  },
  shape: {
    borderRadius: 6,
  },
  typography: {
    fontFamily: SYNMATCH_FONT_FAMILY,
  },
  components: {
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: SYNMATCH_RADII.md },
        standardSuccess: alertSeverityStyles(SYNMATCH_COLORS.status.success),
        standardInfo: alertSeverityStyles(SYNMATCH_COLORS.status.info),
        standardWarning: alertSeverityStyles(SYNMATCH_COLORS.status.warn),
        standardError: alertSeverityStyles(SYNMATCH_COLORS.status.error),
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: SYNMATCH_RADII.sm,
          boxShadow: 'none',
          minHeight: '2.5rem',
          textTransform: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          // Keyboard focus indicator for light admin surfaces; the dark Oxford
          // sidebar gets its own inverse ring in the shell styles.
          '&.Mui-focusVisible': {
            outline: `0.125rem solid ${SYNMATCH_COLORS.primary}`,
            outlineOffset: '0.125rem',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: SYNMATCH_RADII.lg,
          boxShadow: SYNMATCH_SHADOWS.modal,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          gap: '0.75rem',
          padding: '1rem 1.5rem 1.25rem',
        },
        spacing: {
          '& > :not(style) ~ :not(style)': {
            marginLeft: 0,
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        // Oxford-tinted scrim + blur for dialogs only; invisible backdrops
        // (Select/Menu/Popover) must stay transparent.
        root: ({ ownerState }) => ({
          ...(!ownerState.invisible && {
            backgroundColor: SYNMATCH_SCRIM,
            backdropFilter: 'blur(0.25rem)',
          }),
        }),
      },
    },
  },
});

const synmatchGlobalStyles = (
  <GlobalStyles
    styles={{
      '@font-face': {
        fontDisplay: 'swap',
        fontFamily: '"Geist"',
        fontStyle: 'normal',
        fontWeight: '100 900',
        src: `url(${geistVariableFont}) format("woff2")`,
      },
      ':root': {
        '--synmatch-color-accent': SYNMATCH_COLORS.accent,
        '--synmatch-color-border-default': SYNMATCH_COLORS.border.default,
        '--synmatch-color-primary': SYNMATCH_COLORS.primary,
        '--synmatch-color-surface-app': SYNMATCH_COLORS.surface.app,
        '--synmatch-font-family': SYNMATCH_FONT_FAMILY,
        '--synmatch-radius-md': SYNMATCH_RADII.md,
        '--synmatch-radius-sm': SYNMATCH_RADII.sm,
      },
      'html, body, #root': {
        minHeight: '100%',
      },
      body: {
        backgroundColor: SYNMATCH_COLORS.surface.app,
        fontFamily: SYNMATCH_FONT_FAMILY,
        margin: 0,
      },
    }}
  />
);

export const AppThemeProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <MuiThemeProvider theme={synmatchTheme}>
    <StyledComponentsThemeProvider theme={synmatchTheme}>
      <CssBaseline />

      {synmatchGlobalStyles}

      {children}
    </StyledComponentsThemeProvider>
  </MuiThemeProvider>
);
